import { useEffect, useRef, useState } from 'react';

/** ¿Quien mira pidió menos movimiento? Si no se puede saber, se asume que sí. */
function prefiereMenosMovimiento(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Un número que sube hasta su valor al aparecer (la maqueta lo hace en los KPI).
 * Con «reducir movimiento» (o en las pruebas) muestra el valor final de entrada.
 * Si el valor cambia, arranca desde el que se estaba mostrando, no desde cero.
 */
export function useCuentaAnimada(valor: number, duracionMs = 650): number {
  const [mostrado, setMostrado] = useState(() => (prefiereMenosMovimiento() ? valor : 0));
  const desde = useRef(mostrado);

  useEffect(() => {
    if (prefiereMenosMovimiento() || typeof requestAnimationFrame !== 'function') {
      setMostrado(valor);
      desde.current = valor;
      return;
    }
    const inicio = performance.now();
    const origen = desde.current;
    let cuadro = 0;
    const paso = (ahora: number) => {
      const t = Math.min(1, (ahora - inicio) / duracionMs);
      // Sale rápido y frena al final (ease-out cúbico).
      const suave = 1 - Math.pow(1 - t, 3);
      const actual = origen + (valor - origen) * suave;
      setMostrado(actual);
      desde.current = actual;
      if (t < 1) cuadro = requestAnimationFrame(paso);
    };
    cuadro = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(cuadro);
  }, [valor, duracionMs]);

  return mostrado;
}

/**
 * La cifra animada. `formato` recibe el número ya animado; para el lector de
 * pantalla se anuncia siempre el valor final, no los intermedios.
 */
export function CifraAnimada({
  valor,
  formato,
  className,
}: {
  valor: number;
  formato: (n: number) => string;
  className?: string;
}) {
  const mostrado = useCuentaAnimada(valor);
  return (
    <span className={className}>
      <span aria-hidden="true">{formato(mostrado)}</span>
      <span className="sr-only">{formato(valor)}</span>
    </span>
  );
}
